import { CURRENCY, NOT_SET, NO_APLICA, buildEventItemId } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'
import { withIndex } from '@/app/lib/analytics/mapProductToAnalyticsItem'
import type { EcommerceItem, EcommerceItemBase, PriceRedactedEcommerceItem } from '@/app/lib/analytics/types'

import type { SearchProduct } from './types'

/**
 * `SearchProduct` → ítem de e-commerce SIN `index`, para el módulo "Productos
 * más buscados" del overlay del buscador
 * (`components-fingerprint/Resultados de búsqueda.csv` → «Renderizado módulo
 * Productos más buscados»). Deliberadamente separado de
 * `mapProductToAnalyticsItemBase` (`lib/analytics/`, espera `ProductApiItem`)
 * y de `mapSearchItemToAnalyticsItemBase` (`entradas/(general-web)/search/`,
 * espera `SearchResultItem` de `/search`): `SearchProduct` es el shape propio
 * de `module_search.products_most_searched` (`/layout`).
 *
 * ⚠️ A diferencia de esos dos mappers, este CSV manda `item_brand` a
 * `no_aplica` TAMBIÉN en `view_item_list` (línea "Renderizado módulo…"), no
 * solo en `select_item` — se respeta tal cual documenta la ficha, aunque
 * contradiga el criterio general de `not_set` en impresiones que sigue el
 * resto de listados del repo.
 */
export function mapSearchProductToAnalyticsItemBase(product: SearchProduct): EcommerceItemBase {
  return {
    item_id: buildEventItemId(product.url),
    item_name: slugify(product.title),
    item_brand: NO_APLICA,
    item_variant: product.province ? slugify(product.province) : NOT_SET,
    item_category: product.category || NOT_SET,
    item_category2: NO_APLICA,
    price: product.min_price ?? product.price,
    currency: CURRENCY,
    start_date: NO_APLICA,
    artist: NO_APLICA,
  }
}

/**
 * `SearchProduct` → `items[]` de `view_item_list`. `index` entra 0-based
 * (posición en el array acumulado) y sale 1-based, como el resto de listados.
 */
export function mapSearchProductToAnalyticsItem(product: SearchProduct, index: number): EcommerceItem {
  return withIndex(mapSearchProductToAnalyticsItemBase(product), index)
}

/**
 * Ítem de `select_item` de este módulo (`Resultados de búsqueda.csv` → «Click
 * en card de Productos más buscados»): el CSV redacta a `no_aplica` casi
 * todos los campos — solo sobreviven `item_id`/`item_name`/`index` —, a
 * diferencia del `select_item` genérico (`toSelectItem`,
 * `lib/analytics/mapProductToAnalyticsItem.ts`), que conserva
 * `item_variant`/`item_category`/`price`/`currency`. NO se reutiliza
 * `toSelectItem()` aquí por eso: esta ficha es más estricta.
 *
 * `price` pasa de `number` a `string` (`PriceRedactedEcommerceItem`) porque el
 * propio CSV documenta su valor como el literal `no_aplica` — a diferencia de
 * todos los demás eventos de e-commerce del repo, donde `price` es siempre
 * numérico.
 */
export function toSearchProductSelectItem(item: EcommerceItem): PriceRedactedEcommerceItem {
  return {
    ...item,
    item_brand: NO_APLICA,
    item_variant: NO_APLICA,
    item_category: NO_APLICA,
    item_category2: NO_APLICA,
    price: NO_APLICA,
    currency: NO_APLICA,
    start_date: NO_APLICA,
    artist: NO_APLICA,
  }
}
